import React, { useEffect, useState } from "react";
import { Bot, Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useConfirm, type ConfirmOptions } from "@/components/ui/use-confirm";
import { cn } from "@/lib/utils";
import {
  deleteStrategyRdHypothesis,
  listStrategyRdHypothesisVersions,
} from "../../../services/strategyRdApi";
import type { StrategyRdHypothesisVersion } from "../../../types/strategyRd";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { StrategyRdHoldoutVerdictChip, strategyRdHeadlineBrief } from "./StrategyRdHoldoutRow";
import { strategyRdBrandActionButtonClassName } from "./strategyRdCardShell";
import { StrategyRdBadge, StrategyRdLabCard } from "./strategyRdLabUi";
import { StrategyRdLibraryDialog } from "./StrategyRdLibraryDialog";

/** Saved backtests: 100 at most, nothing deleted automatically. */
export const STRATEGY_RD_HYPOTHESIS_LIMIT = 100;

function TesBadge(): JSX.Element {
  return (
    <StrategyRdBadge tone="brand" className="gap-1" title="Made by TES" data-strategy-rd-tes-version="">
      <Bot className="size-3" aria-hidden /> TES
    </StrategyRdBadge>
  );
}

async function confirmAndDelete(
  confirm: (o: ConfirmOptions) => Promise<boolean>,
  id: string,
  name: string
): Promise<boolean> {
  const ok = await confirm({
    title: `Delete “${name}”?`,
    description: "Its trades are deleted too. This can't be undone.",
  });
  if (!ok) return false;
  try {
    await deleteStrategyRdHypothesis(id);
    return true;
  } catch (err) {
    presentMutationError(err, "Could not delete the backtest");
    return false;
  }
}

/**
 * Versions of the open backtest's strategy (§10.3): one row per version, TES-made ones
 * marked, with stats and the hold-out verdict. Open / delete per row.
 */
export function StrategyRdVersionsPanel({
  hypothesisId,
  refreshToken = 0,
  timeZone,
  onOpen,
  onDeleted,
  onShowAll,
}: {
  hypothesisId: string | null;
  /** Bump to reload (a TES run finished, a backtest ran). */
  refreshToken?: number;
  timeZone?: string;
  onOpen: (id: string) => void;
  onDeleted: (id: string) => void;
  onShowAll: () => void;
}): JSX.Element | null {
  const [versions, setVersions] = useState<StrategyRdHypothesisVersion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  useEffect(() => {
    if (!hypothesisId) {
      setVersions(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    listStrategyRdHypothesisVersions(hypothesisId, controller.signal)
      .then(setVersions)
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setVersions(null);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [hypothesisId, refreshToken]);

  if (!hypothesisId) return null;

  return (
    <StrategyRdLabCard
      title="Versions"
      description="Every run of this strategy, newest first. Open one to compare it with the original."
      actions={
        <>
          {loading ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden /> : null}
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className={strategyRdBrandActionButtonClassName}
            onClick={onShowAll}
          >
            <span className="normal-case tracking-normal">Backtest history</span>
          </Button>
        </>
      }
      data-strategy-rd-versions=""
    >
      {versions && versions.length > 0 ? (
        <ul className="flex flex-col divide-y divide-border overflow-hidden rounded-[6px] border border-border">
          {versions.map((v) => {
            const current = v.id === hypothesisId;
            return (
              <li
                key={v.id}
                className={cn(
                  "group flex items-center gap-3 px-4 py-3 transition-colors",
                  current ? "bg-[color:var(--brand-tropical-surf)]/8" : "hover:bg-muted/50"
                )}
                data-current={current ? "" : undefined}
              >
                <span
                  className={cn(
                    "flex h-7 min-w-9 shrink-0 items-center justify-center rounded-[6px] border px-1.5",
                    "font-[family-name:var(--font-secondary)] text-xs font-semibold tabular-nums",
                    current
                      ? "border-[color:var(--brand-tropical-surf)] text-[color:var(--brand-tropical-surf)]"
                      : "border-border text-muted-foreground"
                  )}
                >
                  v{v.version ?? 1}
                </span>
                <button
                  type="button"
                  className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
                  disabled={current}
                  onClick={() => onOpen(v.id)}
                  title={current ? "Open now" : "Open this version in the Lab"}
                >
                  <span className="flex min-w-0 items-center gap-2 font-[family-name:var(--font-primary)] text-sm text-[color:var(--brand-tropical-surf)]">
                    <span className="truncate font-medium">{v.name}</span>
                    {v.created_by === "tes" ? <TesBadge /> : null}
                    {current ? <StrategyRdBadge tone="neutral">Open</StrategyRdBadge> : null}
                  </span>
                  <span className="font-[family-name:var(--font-secondary)] text-xs text-muted-foreground">
                    {strategyRdHeadlineBrief(v.stats)}
                    {v.created_at ? ` · ${formatTradeDateTimeForDisplay(v.created_at, timeZone)}` : ""}
                  </span>
                </button>
                <StrategyRdHoldoutVerdictChip verdict={v.split?.verdict} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100"
                  aria-label={`Delete ${v.name}`}
                  onClick={() =>
                    void confirmAndDelete(confirm, v.id, v.name).then((ok) => {
                      if (!ok) return;
                      setVersions((prev) => (prev ?? []).filter((x) => x.id !== v.id));
                      onDeleted(v.id);
                    })
                  }
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {confirmDialog}
    </StrategyRdLabCard>
  );
}

/** Every saved backtest, grouped by idea, with pin / archive / delete (the Lab library). */
export const StrategyRdSavedBacktestsDialog = StrategyRdLibraryDialog;
